// Behavioral descriptions for PI factor relationships
export interface BehavioralTrait {
  name: string;
  greaterDescription: string;
  greaterLabel: string;
  lesserDescription: string;
  lesserLabel: string;
  // Detailed descriptions for different strength levels
  crossingMidpoint: {
    greater: string;
    lesser: string;
  };
  sameSide: {
    bothLow: string;
    bothHigh: string;
  };
  greaterPercentage?: number; // Percentage of population with greater trait
  lesserPercentage?: number;  // Percentage of population with lesser trait
}

export const factorRelationships: Record<string, BehavioralTrait> = {
  'B:D': {
    name: 'Interaction Style',
    greaterLabel: 'Informal',
    greaterDescription: 'Extraverted, outgoing, uninhibited expression of friendliness, poised, informal in social situations, enthusiastic persuasive talker, engaging conversationalist',
    lesserLabel: 'Formal',
    lesserDescription: 'Serious, disciplined, sincere, reserved/formal and quiet, factual conversationalist, sensitive to criticism, cautious with new people',
    crossingMidpoint: {
      greater: 'Extraverted, outgoing, uninhibited expression of friendliness, poised, informal in social situations, enthusiastic, persuasive talker, engaging conversationalist',
      lesser: 'Serious, disciplined, sincere, reserved, formal and quiet, factual conversationalist, sensitive to criticism, cautious with new people'
    },
    sameSide: {
      bothLow: 'Informal and independent. Imaginative, focused on technical matters of their own choosing. Speaks about concepts and other intangibles. Relatively comfortable delegating details. Warm, lively, social and very talkative. Builds relationship easily while generally engaging within proper bounds of social interaction. Some delegation of detail with strong, friendly follow-up',
      bothHigh: 'Reserved and private. Uses few words to get the point across. Generally speaks about ideas and concepts that interest them. Unencumbered by social rules. Formal, social and proper. Stays within their idea of acceptable social boundaries while collaborating and advancing relationships. Prefers to work together rather than delegating details. Detailed and talkative in communication'
    },
  },
  'A:D': {
    name: 'Risk Tolerance',
    greaterLabel: 'Comfortable with risk',
    greaterDescription: 'Independent, individualistic, self-confident, firm, decisive, venturesome, resistant to authority',
    lesserLabel: 'Careful with risk',
    lesserDescription: 'Cooperative, supportive, willing and helpful, need for rules and structure, accurate and careful, concerned about criticism, conservative',
    crossingMidpoint: {
      greater: 'Independent, individualistic, self-confident, firm, decisive, venturesome, resistant to authority',
      lesser: 'Cooperative, supportive, willing and helpful, need for rules and structure, accurate and careful, concerned about criticism, conservative'
    },
    sameSide: {
      bothLow: 'Free delegation of details and freewheeling approach to "the book". Less emphasis on independence, individualism, and self-confidence. Emphasis on cooperation and supportiveness. A willing and helpful worker. Less need for adhering to rules, more casual about details',
      bothHigh: 'Retains emphasis on independence, individualism, and self-confidence. Seeks control not only of results/outcomes but also specifics/details of the process. More demanding about how things are done. Less risk-tolerant, needs more information to take action. Strong need to go "by the book," do it right. Concern for independence and autonomy within their defined area of responsibility. Demand for results based on a conservative interpretation of the rules. Accuracy, thoroughness'
    },
  },
  'B:C': {
    name: 'Connection Speed',
    greaterLabel: 'Quick to connect',
    greaterDescription: 'Fluent, fast-talking, lively and enthusiastic, optimistic style of expression, persuasive, motivates others, stimulating, positive communication',
    lesserLabel: 'Takes time to connect',
    lesserDescription: 'Reserved, quiet, serious with unfamiliar people, comfortable with the familiar, introspective, takes time to think, organizes thinking before expressing self',
    crossingMidpoint: {
      greater: 'Fluent, fast-talking, lively and enthusiastic, optimistic style of expression, persuasive, motivates others, stimulating, positive communication',
      lesser: 'Reserved, quiet, serious with unfamiliar people, comfortable with the familiar, introspective, takes time to think, organizes thinking before expressing self'
    },
    sameSide: {
      bothLow: 'Sense of urgency and drive to get things done focused mostly on tasks rather than people. Impatient with routines. Organizes thoughts mentally, then takes action quickly when ready. Somewhat reserved in initial contact, less reserved as familiarity increases. Focused on tasks rather than social matters with a drive to get things done quickly. Reserved and serious, especially around new people. Quiet and thoughtful, thinks before speaking. Generally discusses work topics, not personal ones',
      bothHigh: 'Warm, open, very talkative. Proactive in building relationships in a steady, calm way through conversation. Focused listening and willingness to share. Generally upbeat manner. Focused on social matters rather than things or tasks. Patient, calm, and easygoing with others. Unhurried in forming new relationships. Listens well, is open and responsive. Comfortable and talkative with others, particularly familiar colleagues, family and friends'
    },
  },
  'C:D': {
    name: 'Rule Adherence',
    greaterLabel: 'Casual with rules',
    greaterDescription: 'Persistent, casual, stable, limited concern about rules or details, comfortable with ambiguity, easygoing, relaxed',
    lesserLabel: 'Careful with rules',
    lesserDescription: 'Conscientious, thorough, precise, concerned with rules and accuracy, strong follow-up, strict about punctuality and correctness, comfortable with clarity',
    crossingMidpoint: {
      greater: 'Persistent, casual, stable, limited concern about rules or details, comfortable with ambiguity, easygoing, relaxed',
      lesser: 'Conscientious, thorough, precise, concerned with rules and accuracy, strong follow-up, strict about punctuality and correctness, comfortable with clarity'
    },
    sameSide: {
      bothLow: 'Uninhibited, unconcerned about "the book". Delegates details very freely with little follow-up. Focused on overall idea of "big picture". Not interested in advanced planning. Takes things as they come and adapts. Comfortable with ambiguity. Won\'t take no for an answer. Will handle some details if the job requires it, but prefers to delegate when possible. Will follow up on delegated assignments. Although very independent, able to adjust to corporate standards, values, and adhere to rules to a degree sufficient for most organizations',
      bothHigh: 'Patient, stable, and easygoing. Aptitude for detail somewhat better than average. Moderately concerned about "the book" but adaptable to change. Doesn\'t rock the boat. Tolerant with people and routines. Accurate, thorough, methodical, stable, careful, and conscientious. Concerned with the quality of work and correctness in terms of rules and standards. Depends on proven methods and accepted principles, respects authority. Conservative, cautious, prudent. Very reluctant to delegate details. Detailed in communication'
    },
  },
  'A:C': {
    name: 'Action Approach',
    greaterLabel: 'Proactive',
    greaterDescription: 'Takes initiative, competitive, driven to get things done, positive response to pressure, fast-paced, achievement-oriented, impatient with routine',
    lesserLabel: 'Responsive',
    lesserDescription: 'Consistent with repetitive work, cooperative with others, tolerant, patient, dependable, steady, easygoing',
    crossingMidpoint: {
      greater: 'Takes initiative, competitive, driven to get things done, positive response to pressure, fast-paced, achievement-oriented, impatient with routine',
      lesser: 'Consistent with repetitive work, cooperative with others, tolerant, patient, dependable, steady, easygoing'
    },
    sameSide: {
      bothLow: 'Initiative and proactive action when it\'s clear that actions will help team efforts. Sense of urgency and drive to get things done quickly and supportively. Contributes to company/team agenda by multitasking and avoiding routines. Responsive and quick to act to contribute to agreed agenda. Generally ready to spring into action to lend a hand or respond to the needs of the job or others',
      bothHigh: 'Assertive and proactive in a steady, methodical way. Pursues own goals and agenda, at own pace. Demanding and openly challenging with a persistent, even-tempered approach. Composed, unruffled, and systematic in pursuing own goals. Persistent and self-confident. Steadily and calmly keeps own agenda moving forward, one step at a time'
    },
  },
  'A:B': {
    name: 'Work Orientation',
    greaterLabel: 'Task Oriented',
    greaterDescription: 'Critical creative thinker, technical orientation, inquiring mind, problem solver, limited delegation of authority',
    lesserLabel: 'People Oriented',
    lesserDescription: 'Empathetic, persuasive, sociable, service-oriented, delegates authority, comfortable on a team, unselfish',
    crossingMidpoint: {
      greater: 'Critical, creative thinker, technical orientation, inquiring mind, problem solver, limited delegation of authority',
      lesser: 'Empathetic, persuasive, sociable, service-oriented, delegates authority, comfortable on a team, unselfish'
    },
    sameSide: {
      bothLow: 'More Analytical, less venturesome. More cautious and practical. Ingenious problem solving and more detailed. Less talk, more discipline. Some social orientation but less interactive. Friendly in a quiet, unassuming way. Seeks harmonious involvement with team',
      bothHigh: 'Less introspection, more input from others. More venturesome, highly inquisitive. Authoritative with some delegation. More enthusiasm, persuasiveness. More assertive and goal-orientation. Will delegate authority but demand results. Seeks more leadership'
    },
  },
};

export function getRelationshipKey(factor1: string, factor2: string): string {
  // Normalize the key to match our mapping (alphabetical order)
  const factors = [factor1.toUpperCase(), factor2.toUpperCase()].sort();
  return `${factors[0]}:${factors[1]}`;
}

export function evaluateRelationship(
  factor1: string, 
  value1: number, 
  factor2: string, 
  value2: number
): {
  trait: BehavioralTrait;
  candidateHas: 'greater' | 'lesser' | 'balanced';
  description: string;
  detailedDescription: string;
  label: string;
  strength: 'strong' | 'moderate' | 'slight' | 'balanced';
  expressionType: 'crossing' | 'sameSide' | 'balanced';
} {
  const key = getRelationshipKey(factor1, factor2);
  const trait = factorRelationships[key];
  
  if (!trait) {
    throw new Error(`No behavioral description found for ${factor1}:${factor2}`);
  }

  // Determine which factor should be greater based on the key
  const [firstFactor] = key.split(':');
  let diff: number;
  
  if (factor1.toUpperCase() === firstFactor) {
    diff = value1 - value2;
  } else {
    diff = value2 - value1;
  }

  // Determine strength and direction
  const absDiff = Math.abs(diff);
  let strength: 'strong' | 'moderate' | 'slight' | 'balanced';
  
  if (absDiff >= 20) {
    strength = 'strong';
  } else if (absDiff >= 10) {
    strength = 'moderate';
  } else if (absDiff >= 5) {
    strength = 'slight';
  } else {
    strength = 'balanced';
  }

  if (strength === 'balanced') {
    return {
      trait,
      candidateHas: 'balanced',
      description: `Balanced between ${trait.greaterLabel.toLowerCase()} and ${trait.lesserLabel.toLowerCase()} tendencies`,
      detailedDescription: `Balanced between ${trait.greaterLabel.toLowerCase()} and ${trait.lesserLabel.toLowerCase()} tendencies`,
      label: 'Balanced',
      strength: 'balanced',
      expressionType: 'balanced',
    };
  }

  const candidateHas = diff > 0 ? 'greater' : 'lesser';
  let description: string;
  let detailedDescription: string;
  let expressionType: 'crossing' | 'sameSide';

  // Determine if factors are crossing midpoint (different sides of 50) or same side
  const midpoint = 50;
  const bothAboveMidpoint = value1 > midpoint && value2 > midpoint;
  const bothBelowMidpoint = value1 < midpoint && value2 < midpoint;
  const sameSide = bothAboveMidpoint || bothBelowMidpoint;

  if (sameSide) {
    // Same side of midpoint - moderate expression
    expressionType = 'sameSide';
    if (bothBelowMidpoint) {
      detailedDescription = trait.sameSide.bothLow;
    } else {
      detailedDescription = trait.sameSide.bothHigh;
    }
    description = candidateHas === 'greater' ? trait.greaterDescription : trait.lesserDescription;
  } else {
    // Crossing midpoint - stronger expression
    expressionType = 'crossing';
    if (candidateHas === 'greater') {
      detailedDescription = trait.crossingMidpoint.greater;
      description = trait.greaterDescription;
    } else {
      detailedDescription = trait.crossingMidpoint.lesser;
      description = trait.lesserDescription;
    }
  }

  const label = candidateHas === 'greater' ? trait.greaterLabel : trait.lesserLabel;

  return {
    trait,
    candidateHas,
    description,
    detailedDescription,
    label,
    strength,
    expressionType,
  };
}

export function getAllBehavioralTraits(
  factorA: number,
  factorB: number,
  factorC: number,
  factorD: number
): Record<string, ReturnType<typeof evaluateRelationship>> {
  const traits: Record<string, ReturnType<typeof evaluateRelationship>> = {};
  
  // Evaluate all 6 combinations
  traits['A:B'] = evaluateRelationship('A', factorA, 'B', factorB);
  traits['A:C'] = evaluateRelationship('A', factorA, 'C', factorC);
  traits['A:D'] = evaluateRelationship('A', factorA, 'D', factorD);
  traits['B:C'] = evaluateRelationship('B', factorB, 'C', factorC);
  traits['B:D'] = evaluateRelationship('B', factorB, 'D', factorD);
  traits['C:D'] = evaluateRelationship('C', factorC, 'D', factorD);
  
  return traits;
}